import React, { useState } from "react";
import { Plan } from "../api/client";
import ThreadWorkspace from "./ThreadWorkspace";

type Props = {
  displayName: string;
  threadId: number | null;
  onPrepareWorkspace: () => Promise<number>;
  onOpenProject: (plan: Plan, wellbeingPromptsEnabled: boolean) => void;
};

export default function FirstLoginOnboarding({
  displayName,
  threadId,
  onPrepareWorkspace,
  onOpenProject,
}: Props) {
  const [stage, setStage] = useState<"welcome" | "method" | "choice" | "project">("welcome");
  const [wellbeingPromptsEnabled, setWellbeingPromptsEnabled] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function beginProject() {
    setPreparing(true);
    setError(null);
    try {
      await onPrepareWorkspace();
      setStage("project");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not prepare your workspace");
    } finally {
      setPreparing(false);
    }
  }

  if (stage === "project") {
    return (
      <section className="onboarding-page onboarding-page--wide">
        <div className="onboarding-progress" aria-label="Introduction progress">
          <span className="onboarding-progress__done">1</span>
          <span className="onboarding-progress__done">2</span>
          <span className="onboarding-progress__done">3</span>
          <span className="onboarding-progress__current">4</span>
        </div>
        <p className="onboarding-kicker">YOUR FIRST REAL PROJECT</p>
        <h1>Let’s turn what you brought into clear steps</h1>
        <p className="onboarding-lead">
          This is not a practice exercise. The project you create now will be saved in your Workspace.
        </p>
        {threadId !== null ? (
          <ThreadWorkspace
            threadId={threadId}
            introduction
            onOpenProject={(plan) => onOpenProject(plan, wellbeingPromptsEnabled)}
          />
        ) : <p>Preparing your workspace…</p>}
      </section>
    );
  }

  return (
    <section className="onboarding-page">
      <div className="onboarding-progress" aria-label="Introduction progress">
        {["welcome", "method", "choice"].map((item, index) => (
          <span
            key={item}
            className={stage === item ? "onboarding-progress__current" : "onboarding-progress__done"}
          >
            {index + 1}
          </span>
        ))}
        <span>4</span>
      </div>

      {stage === "welcome" ? (
        <>
          <p className="onboarding-kicker">WELCOME TO THE SANCTUARY</p>
          <h1>Welcome, {displayName}</h1>
          <p className="onboarding-lead">
            You joined for help. Start with the real problem, task, project or dream that brought you here.
          </p>
          <div className="onboarding-callout">
            <strong>You remain in control.</strong>
            <p>
              Sanctuary gives you structure, practical instruction and support. You choose where you are going, what you work on and when you change direction.
            </p>
          </div>
          <div className="onboarding-downloads">
            <span>Prefer to follow on paper or in Word?</span>
            <a href="/sanctuary-getting-started-workbook.pdf" download>Printable workbook</a>
            <a href="/sanctuary-getting-started-workbook.docx" download>Editable workbook</a>
          </div>
          <button className="onboarding-primary" type="button" onClick={() => setStage("method")}>
            Show me how it works →
          </button>
        </>
      ) : null}

      {stage === "method" ? (
        <>
          <p className="onboarding-kicker">HOW SANCTUARY HELPS</p>
          <h1>One project. One manageable step at a time.</h1>
          <div className="onboarding-method-grid">
            <article><span>1</span><h2>Bring what matters</h2><p>Describe the result, problem or project in your own words.</p></article>
            <article><span>2</span><h2>Get clear steps</h2><p>Sanctuary breaks it down so you can see a practical route forward.</p></article>
            <article><span>3</span><h2>Ask until it is clear</h2><p>Ask Rook one question or ten. The conversation stays with that step.</p></article>
            <article><span>4</span><h2>Keep useful answers</h2><p>Copy replies to your notes and turn the discussion into editable instructions.</p></article>
          </div>
          <div className="onboarding-callout">
            <strong>You can pause without losing your place.</strong>
            <p>
              If a step needs research or a skill you do not yet have, start a supporting project, learn what you need, then return to the main project whenever you are ready.
            </p>
          </div>
          <div className="onboarding-actions">
            <button type="button" onClick={() => setStage("welcome")}>← Back</button>
            <button className="onboarding-primary" type="button" onClick={() => setStage("choice")}>Continue →</button>
          </div>
        </>
      ) : null}

      {stage === "choice" ? (
        <>
          <p className="onboarding-kicker">YOUR CHOICE</p>
          <h1>Would you like wellbeing check-in reminders?</h1>
          <p className="onboarding-lead">
            The Check-In is always available. If you choose reminders, Sanctuary can offer a quiet prompt on Home when you have not checked in that day.
          </p>
          <div className="onboarding-choice-grid">
            <button
              type="button"
              className={wellbeingPromptsEnabled ? "onboarding-choice onboarding-choice--selected" : "onboarding-choice"}
              onClick={() => setWellbeingPromptsEnabled(true)}
            >
              <strong>Yes, offer the reminder</strong>
              <span>I can still ignore it or change this later.</span>
            </button>
            <button
              type="button"
              className={!wellbeingPromptsEnabled ? "onboarding-choice onboarding-choice--selected" : "onboarding-choice"}
              onClick={() => setWellbeingPromptsEnabled(false)}
            >
              <strong>No reminders</strong>
              <span>Wellbeing remains available when I choose it.</span>
            </button>
          </div>
          <p className="onboarding-reassurance">There is no wrong answer. This is your journey and your choice.</p>
          {error ? <p className="workspace-error">{error}</p> : null}
          <div className="onboarding-actions">
            <button type="button" onClick={() => setStage("method")}>← Back</button>
            <button className="onboarding-primary" type="button" onClick={beginProject} disabled={preparing}>
              {preparing ? "Preparing your workspace…" : "Create my first project →"}
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
